import {
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { User } from '../../models/user.model';
import { CommonModule } from '@angular/common';
import {
  BillingInterval,
  FounderOffer,
  Subscription,
} from '../../models/subscription.model';
import { ApiService } from '../../services/api.service';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PlanFeatureService } from '../../services/plan-feature.service';
import { LogService } from '../../services/log.service';
import { AnalyticsService } from '../../services/analytics.service';

@Component({
  selector: 'app-subscription',
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.css'],
})
export class SubscriptionComponent implements OnInit {
  private log = inject(LogService);
  private analytics = inject(AnalyticsService);
  private cdr = inject(ChangeDetectorRef);
  readonly planFeatureService = inject(PlanFeatureService);

  /**
   * DÉTAILS: On utilise des 'signals' pour stocker les informations de l'utilisateur et de son abonnement.
   * L'avantage est que le template se mettra à jour automatiquement dès que ces signaux reçoivent de nouvelles valeurs.
   */
  user = signal<Partial<User> | null>(null);
  subscription = signal<Subscription | null>(null);
  isPortalLoading = false;

  // --- Gestion des Modales ---
  showChangePlanModal = false;
  showCancelModal = false;
  showFinalCancelModal = false;
  showPauseModal = false;
  private openPauseOnLoad = false;

  // --- Données pour le changement de plan ---
  targetPlan: string = '';

  /** Prix mensuel par plan (pour affichage modal) */
  readonly planPrices: Record<string, string> = {
    free: '0€',
    starter: '19,90€',
    pro: '29,90€',
    business: '49,90€',
  };

  // --- Facturation annuelle (2 mois offerts) et tarif fondateur (-30 % à vie) ---
  private readonly monthlyAmounts: Record<string, number> = {
    starter: 19.9,
    pro: 29.9,
    business: 49.9,
  };
  private readonly annualMonthsPaid = 10;
  private readonly founderFactor = 0.7;

  selectedInterval: BillingInterval = 'month';
  founderOffer = signal<FounderOffer | null>(null);

  /** Un nouvel abonnement peut choisir sa périodicité ; un changement de plan garde celle en cours. */
  get canChooseInterval(): boolean {
    return this.isFree || this.isPaused || this.isSubscriptionBlocked;
  }

  get effectiveInterval(): BillingInterval {
    return this.canChooseInterval
      ? this.selectedInterval
      : (this.subscription()?.billingInterval ?? 'month');
  }

  get isFounder(): boolean {
    return !!this.subscription()?.founder;
  }

  get isPilot(): boolean {
    return !!this.user()?.isPilot;
  }

  /** Offre fondateur applicable : places restantes, remise déjà acquise, ou place réservée à un pilote. */
  get founderApplies(): boolean {
    return (
      this.canChooseInterval &&
      (this.isFounder || this.isPilot || !!this.founderOffer()?.enabled)
    );
  }

  private formatPrice(amount: number): string {
    return (
      amount.toLocaleString('fr-FR', {
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
      }) + '€'
    );
  }

  private amountFor(planId: string): number {
    const monthly = this.monthlyAmounts[planId.toLowerCase()] ?? 0;
    return this.effectiveInterval === 'year'
      ? monthly * this.annualMonthsPaid
      : monthly;
  }

  get priceUnit(): string {
    return this.effectiveInterval === 'year' ? '/an' : '/mois';
  }

  priceLabel(planId: string): string {
    return this.formatPrice(this.amountFor(planId));
  }

  /** Équivalent mensuel de l'annuel (2 mois offerts). */
  annualHint(planId: string): string | null {
    if (this.effectiveInterval !== 'year') return null;
    return `soit ${this.formatPrice(this.amountFor(planId) / 12)}/mois · 2 mois offerts`;
  }

  /** Prix après remise fondateur, uniquement quand elle s'applique. */
  founderHint(planId: string): string | null {
    if (!this.founderApplies) return null;
    return `${this.formatPrice(this.amountFor(planId) * this.founderFactor)}${this.priceUnit} à vie avec le tarif fondateur`;
  }

  /** Label du plan courant (uppercase) */
  get currentPlanLabel(): string {
    return this.subscription()?.plan?.toUpperCase() || 'FREE';
  }

  /** Label du plan cible (uppercase) */
  get targetPlanLabel(): string {
    return this.targetPlan?.toUpperCase() || '';
  }

  // --- Données pour l'annulation ---
  selectedCancelReason: string = '';
  cancelReasons: { label: string; value: string }[] = [
    { label: 'Trop cher', value: 'too_expensive' },
    { label: "Je ne l'utilise pas assez", value: 'not_using_enough' },
    { label: 'Manque de fonctionnalités', value: 'missing_features' },
    { label: 'Je passe chez un concurrent', value: 'switching_competitor' },
    { label: 'Autre', value: 'other' },
  ];

  // --- Essai expiré (redirigé depuis interceptor) ---
  trialExpired = false;

  // --- Abonnement en pause (redirigé depuis interceptor) ---
  pausedRedirect = false;

  /** Pause programmée : accès maintenu jusqu'à la fin de la période payée */
  get isPauseScheduled(): boolean {
    return this.subscription()?.pauseStatus === 'scheduled';
  }

  /** Pause effective : plan repassé à free, données conservées */
  get isPaused(): boolean {
    return this.subscription()?.pauseStatus === 'paused';
  }

  // --- Échec de paiement (redirigé depuis interceptor) ---
  paymentFailed = false;

  // --- Paiement en retard (past_due) ---
  get isPastDue(): boolean {
    return this.subscription()?.status === 'past_due';
  }

  // --- Abonnement bloqué (unpaid/canceled) ---
  get isSubscriptionBlocked(): boolean {
    const status = this.subscription()?.status;
    return (status === 'unpaid' || status === 'canceled') && !this.isFree;
  }

  // Plan gratuit (période d'essai)
  get isFree(): boolean {
    const plan = this.subscription()?.plan?.toLowerCase();
    return !plan || plan === 'free';
  }

  /** Abonnement payant actif, sans résiliation programmée : pause possible */
  get canPause(): boolean {
    return (
      !this.isFree &&
      this.subscription()?.status === 'active' &&
      !this.subscription()?.cancelAtPeriodEnd
    );
  }

  /** Type de marchand (fixe ou itinérant) */
  get isFixedMerchant(): boolean {
    return this.user()?.merchantType === 'fixed';
  }

  get isItinerantMerchant(): boolean {
    return this.user()?.merchantType === 'itinerant';
  }

  /** Comparaison case-insensitive du plan courant */
  currentPlanIs(planId: string): boolean {
    return (
      (this.subscription()?.plan?.toLowerCase() || 'free') ===
      planId.toLowerCase()
    );
  }

  /** Vérifie si un plan est le plan cible d'un downgrade programmé */
  isPendingPlan(planId: string): boolean {
    return (
      !!this.subscription()?.pendingPlan &&
      this.subscription()!.pendingPlan!.toLowerCase() === planId.toLowerCase()
    );
  }

  /** Vérifie si un downgrade est en cours */
  get hasPendingDowngrade(): boolean {
    return (
      !!this.subscription()?.pendingPlan &&
      !this.subscription()?.cancelAtPeriodEnd
    );
  }

  get trialEndDate(): Date | null {
    const user = this.user();
    if (!user) return null;
    // trialEndDate vient du backend (virtual Mongoose)
    if ((user as any).trialEndDate) {
      return new Date((user as any).trialEndDate);
    }
    // Fallback: createdAt + 1 mois (30 jours, comme le backend)
    if (user.createdAt) {
      const end = new Date(user.createdAt);
      end.setDate(end.getDate() + 30);
      return end;
    }
    return null;
  }

  // --- Retour de Stripe ---
  paymentSuccess = false;
  paymentLoading = false;

  // --- Confirmation changement de plan PAID→PAID ---
  planChangeSuccess = false;
  planChangeMessage = '';

  /**
   * Plan recommandé pour la conversion post-trial.
   * Priorité: selectedPlan (choisi à l'inscription) sinon 'pro'
   */
  get recommendedPlan(): string {
    return this.planFeatureService.selectedPlan() || 'pro';
  }

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    // Vérifier si redirigé pour essai expiré ou échec de paiement
    const reason = this.route.snapshot.queryParamMap.get('reason');
    this.trialExpired = reason === 'trial_expired';
    this.pausedRedirect = reason === 'paused';
    this.paymentFailed = reason === 'payment_failed';
    // Lien « Mettre en pause » de « Mon profil » (BUG-04, D-08)
    this.openPauseOnLoad =
      this.route.snapshot.queryParamMap.get('action') === 'pause';

    this.user.set(this.authService.getCurrentUser());

    // Compteur réel de l'offre fondateur (silencieux si indisponible)
    this.apiService.getFounderOffer().subscribe({
      next: (offer) => this.founderOffer.set(offer),
      error: () => this.founderOffer.set(null),
    });

    // Retour de Stripe: vérifier et activer la session
    const status = this.route.snapshot.queryParamMap.get('status');
    const sessionId = this.route.snapshot.queryParamMap.get('session_id');

    if (status === 'success' && sessionId) {
      this.verifyAndActivate(sessionId);
    } else {
      // Nettoyer l'URL si on arrive avec ?reason= pour ne pas rester bloqué
      if (reason || this.openPauseOnLoad) {
        this.router.navigate(['/subscription'], { replaceUrl: true });
      }
      this.loadSubscription();
    }
  }

  /**
   * Vérifie la session Stripe et active l'abonnement côté backend.
   */
  private verifyAndActivate(sessionId: string) {
    this.paymentLoading = true;
    this.apiService.verifyCheckoutSession(sessionId).subscribe({
      next: (res) => {
        this.paymentLoading = false;
        this.paymentSuccess = true;
        this.trialExpired = false;
        this.analytics.trackPaiement('succes', res.plan);
        if (res.subscription) {
          this.subscription.set(res.subscription);
          this.authService.setSubscription(res.subscription);
        }
        // Mettre à jour le user en sessionStorage avec le nouveau plan
        const currentUser = this.authService.getCurrentUser();
        if (currentUser && res.plan) {
          this.authService.setCurrentUser({
            ...currentUser,
            plan: res.plan,
          } as any);
          this.authService.refreshCurrentUser();
        }
        this.cdr.markForCheck();
        // Nettoyer l'URL (retirer query params)
        this.router.navigate(['/subscription'], { replaceUrl: true });
      },
      error: () => {
        this.paymentLoading = false;
        // Fallback: charger normalement (le webhook a peut-être déjà traité)
        this.loadSubscription();
      },
    });
  }

  loadSubscription() {
    const cachedSub = this.authService.getSubscription();
    if (cachedSub) this.subscription.set(cachedSub);

    // Optionnel: rafraîchir les données de l'abonnement au cas où elles auraient changé
    this.apiService.getSubscription().subscribe({
      next: (sub) => {
        this.subscription.set(sub);
        this.authService.setSubscription(sub); // Met à jour le cache local
        // Une reprise propose par défaut la périodicité d'origine
        if (sub.pauseStatus === 'paused' && sub.billingInterval) {
          this.selectedInterval = sub.billingInterval;
        }
        // Si l'abonnement est actif, effacer les flags d'erreur de paiement
        if (sub.status === 'active' || sub.status === 'trialing') {
          this.paymentFailed = false;
          this.trialExpired = false;
        }
        if (this.openPauseOnLoad) {
          this.openPauseOnLoad = false;
          this.showPauseModal = this.canPause;
        }
        this.cdr.markForCheck();
      },
      error: (err) =>
        this.log.error("Erreur lors de la récupération de l'abonnement", err),
    });
  }

  // --- Logique Changement de Plan ---

  selectPlan(planId: string) {
    const currentPlan = this.subscription()?.plan?.toLowerCase();
    if (currentPlan === 'free') {
      // Utilisateur en essai gratuit → Checkout Stripe
      this.targetPlan = planId;
      this.showChangePlanModal = true;
    } else {
      // Utilisateur déjà payant → Upgrade/Downgrade interne
      this.targetPlan = planId;
      this.showChangePlanModal = true;
    }
  }

  isUpgrade(targetPlanId: string): boolean {
    const weights: { [key: string]: number } = {
      free: 0,
      starter: 1,
      pro: 2,
      business: 3,
    };
    const currentPlan = this.subscription()?.plan?.toLowerCase() || 'free';
    const currentWeight = weights[currentPlan] ?? 0;
    const targetWeight = weights[targetPlanId.toLowerCase()] || 0;
    return targetWeight > currentWeight;
  }

  confirmPlanChange() {
    this.isPortalLoading = true;
    this.apiService
      .updateSubscription(this.targetPlan, this.effectiveInterval)
      .subscribe({
        next: (res) => {
          this.isPortalLoading = false;
          this.showChangePlanModal = false;
          // Cas FREE→PAID : redirection vers Stripe Checkout
          if (res.checkoutUrl) {
            this.analytics.trackAbonnementChange(this.targetPlan);
            window.location.href = res.checkoutUrl;
            return;
          }
          // Cas PAID→PAID (upgrade/downgrade)
          this.analytics.trackAbonnementChange(this.targetPlan);
          if (res.subscription) {
            this.subscription.set(res.subscription);
            this.authService.setSubscription(res.subscription);
          }
          // Mettre à jour le user en cache avec le nouveau plan
          if (res.user?.plan) {
            const currentUser = this.authService.getCurrentUser();
            if (currentUser) {
              this.authService.setCurrentUser({
                ...currentUser,
                plan: res.user.plan,
              } as any);
              this.authService.refreshCurrentUser();
            }
          }
          // Afficher le message de confirmation et scroller en haut
          const planLabel = this.targetPlan?.toUpperCase() || '';
          this.planChangeMessage = this.isUpgrade(this.targetPlan)
            ? `Votre abonnement a été mis à jour vers ${planLabel} ! Profitez de vos nouvelles fonctionnalités.`
            : `Votre abonnement passera à ${planLabel} à la fin de la période en cours.`;
          this.planChangeSuccess = true;
          this.loadSubscription();
          window.scrollTo({ top: 0, behavior: 'smooth' });
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.isPortalLoading = false;
          // Prorata non payé (carte refusée / 3-D Secure) : proposer la page de paiement Stripe
          if (
            err.error?.code === 'UPGRADE_PAYMENT_PENDING' &&
            err.error?.paymentUrl
          ) {
            this.showChangePlanModal = false;
            if (
              confirm(
                `${err.error.message}\n\nFinaliser le paiement maintenant ?`,
              )
            ) {
              window.location.href = err.error.paymentUrl;
              return;
            }
            this.loadSubscription();
            this.cdr.markForCheck();
            return;
          }
          alert(err.error?.message || 'Erreur lors du changement de plan');
          this.cdr.markForCheck();
        },
      });
  }

  // --- Logique Annulation ---

  openCancelModal() {
    this.selectedCancelReason = '';
    this.showCancelModal = true;
  }

  proceedToCancelConfirmation() {
    this.showCancelModal = false;
    this.showFinalCancelModal = true;
  }

  confirmCancellation() {
    this.isPortalLoading = true;
    this.apiService.cancelSubscription(this.selectedCancelReason).subscribe({
      next: (res) => {
        this.isPortalLoading = false;
        this.showFinalCancelModal = false;
        this.analytics.trackAbonnementAnnule(this.selectedCancelReason);
        // Mettre à jour l'état local pour refléter l'annulation programmée
        this.loadSubscription(); // Recharger pour avoir la date de fin exacte
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isPortalLoading = false;
        alert(err.error.message || "Erreur lors de l'annulation");
        this.cdr.markForCheck();
      },
    });
  }

  // --- Autres ---

  // --- Pause d'abonnement ---

  confirmPause() {
    this.isPortalLoading = true;
    this.apiService.pauseSubscription().subscribe({
      next: () => {
        this.isPortalLoading = false;
        this.showPauseModal = false;
        this.planChangeMessage =
          "Pause programmée : vous gardez l'accès jusqu'à la fin de la période payée, vos données sont conservées.";
        this.planChangeSuccess = true;
        this.loadSubscription();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isPortalLoading = false;
        alert(err.error?.message || 'Erreur lors de la mise en pause');
        this.cdr.markForCheck();
      },
    });
  }

  /** Annule la pause programmée, ou reprend (paiement) une pause effective. */
  resumeSubscription() {
    this.isPortalLoading = true;
    const pausedPlan = this.subscription()?.pausedPlan ?? undefined;
    this.apiService
      .resumeSubscription(pausedPlan, this.effectiveInterval)
      .subscribe({
        next: (res) => {
          this.isPortalLoading = false;
          if (res.checkoutUrl) {
            this.analytics.trackAbonnementChange(
              this.subscription()?.pausedPlan || '',
            );
            window.location.href = res.checkoutUrl;
            return;
          }
          this.planChangeMessage = res.message;
          this.planChangeSuccess = true;
          this.loadSubscription();
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.isPortalLoading = false;
          alert(err.error?.message || 'Erreur lors de la reprise');
          this.cdr.markForCheck();
        },
      });
  }

  reactivateSubscription() {
    if (!this.subscription()?.plan) return;

    this.isPortalLoading = true;
    this.apiService.reactivateSubscription().subscribe({
      next: (res) => {
        this.isPortalLoading = false;
        this.loadSubscription();
        alert('Abonnement réactivé avec succès !');
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isPortalLoading = false;
        alert("Impossible de réactiver l'abonnement.");
        this.cdr.markForCheck();
      },
    });
  }

  openStripePortal() {
    this.apiService.createPortalSession().subscribe({
      next: (res) => (window.location.href = res.url),
      error: () => alert("Impossible d'accéder au portail de facturation."),
    });
  }

  /**
   * Renouveler un abonnement bloqué (subscription canceled/unpaid).
   * Crée une nouvelle session Checkout Stripe pour le plan actuel.
   */
  renewBlockedSubscription(): void {
    const plan = this.subscription()?.plan?.toLowerCase() || 'starter';
    if (!plan || plan === 'free') return;

    this.isPortalLoading = true;
    this.apiService
      .updateSubscription(plan, this.subscription()?.billingInterval ?? 'month')
      .subscribe({
        next: (res) => {
          this.isPortalLoading = false;
          if (res.checkoutUrl) {
            window.location.href = res.checkoutUrl;
          } else {
            // Cas inattendu : downgrade traité en DB uniquement
            this.loadSubscription();
            this.cdr.markForCheck();
          }
        },
        error: (err) => {
          this.isPortalLoading = false;
          alert(err.error?.message || 'Erreur lors du renouvellement.');
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * DÉTAILS: Méthode pour créer et rediriger vers le portail client Stripe.
   * 1. Active l'état de chargement.
   * 2. Appelle le backend pour créer une session de portail.
   * 3. Redirige la fenêtre du navigateur vers l'URL de la session retournée par le backend.
   */
  /*
  manageSubscription(): void {
    this.isPortalLoading = true;
    this.apiService.createPortalSession().subscribe({
      next: (response) => {
        // Redirection vers le portail client de Stripe
        window.location.href = response.url;
      },
      error: (err) => {
        this.log.error(
          'Erreur lors de la création de la session du portail client',
          err
        );
        // Afficher une erreur à l'utilisateur ici
        this.isPortalLoading = false;
      },
    });
  }
  */
}
